import {
  isoDateTime,
  type ISODateTime,
  type Minutes,
  type Task,
  type TaskId,
} from '@bigint-project/core';
import { parseDurationInput } from './duration.js';
import { parsePredecessorInput, type ParsedPredecessor } from './predecessor.js';

/**
 * 從 Excel / MS Project 匯出表貼上的解析工具。
 *
 * 典型來源(台電案 WBS 表)欄位順序:
 * `WBS | 任務名稱 | 工期 | 開始時間 | 完成時間 | 前置任務`
 *
 * 設計要點:
 * 1. 階層以 **WBS 碼**為準(`1.4.1` → outlineLevel 3),任務名稱的前導空格只當
 *    第二訊號用來驗證,不一致時以 WBS 為準並記警告。
 * 2. 日期的月/日順序由「整批資料」一次判定後**整批一致套用**,不可逐列各自猜,
 *    否則同一批資料中間幾列會悄悄月日互換而看起來仍然正常。
 * 3. 前置任務必須等**所有列都建立完成之後**再解析(ref `55` 指「完整清單第 55 筆」),
 *    因此解析拆成兩個階段:parseMsProjectPaste(第一階段)與
 *    resolvePastedPredecessors(第二階段)。
 */

/** 每一階大綱在任務名稱前的空格數(MS Project 匯出到 Excel 的固定縮排寬度)。 */
const SPACES_PER_OUTLINE_LEVEL = 3;

/** 兩位年份的世紀基準:00–99 一律映射到 2000–2099。 */
const TWO_DIGIT_YEAR_BASE = 2000;

/** 匯入日期時補上的時間(與 TaskGrid 單格日期編輯的預設一致)。 */
const DEFAULT_TIME_OF_DAY = '08:00:00';

/** 日期的前兩段順序。 */
export type DateOrder = 'mdy' | 'dmy';

export interface DateOrderDetection {
  readonly order: DateOrder;
  /** 整批資料都無法區分月/日(兩段皆 ≤ 12)時為 true,呼叫端必須警告使用者。 */
  readonly ambiguous: boolean;
}

const DATE_SPLIT = /[-/.]/;

/**
 * 掃描整批日期,判定月/日順序。
 *
 * - 任一筆第一段 > 12 → 整批是 `dd-mm`
 * - 任一筆第二段 > 12 → 整批是 `mm-dd`
 * - 全部 ≤ 12(真正歧義)→ 預設 `mm-dd`,並回報 ambiguous
 * - `yyyy-mm-dd`(四位年在前)本身無歧義,不參與判定
 * - 若兩種證據同時出現(資料本身混雜),以 `mm-dd` 為準並回報 ambiguous
 */
export function detectDateOrder(values: readonly string[]): DateOrderDetection {
  let mdyEvidence = false;
  let dmyEvidence = false;

  for (const value of values) {
    const parts = parseSegments(value);
    if (!parts || parts.yearFirst) continue;
    if (parts.a > 12) dmyEvidence = true;
    if (parts.b > 12) mdyEvidence = true;
  }

  if (mdyEvidence && dmyEvidence) return { order: 'mdy', ambiguous: true };
  if (dmyEvidence) return { order: 'dmy', ambiguous: false };
  if (mdyEvidence) return { order: 'mdy', ambiguous: false };
  return { order: 'mdy', ambiguous: true };
}

interface Segments {
  readonly yearFirst: boolean;
  readonly year: number;
  readonly a: number;
  readonly b: number;
}

function parseSegments(text: string): Segments | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const dateOnly = trimmed.split(/[T\s]/)[0] ?? '';
  const segments = dateOnly.split(DATE_SPLIT).filter((s) => s !== '');
  if (segments.length !== 3) return null;
  if (!segments.every((s) => /^\d+$/.test(s))) return null;
  const [s0, s1, s2] = segments as [string, string, string];
  const [n0, n1, n2] = [Number(s0), Number(s1), Number(s2)];

  if (s0.length === 4) {
    return { yearFirst: true, year: n0, a: n1, b: n2 };
  }
  const year = s2.length === 4 ? n2 : n2 < 100 ? TWO_DIGIT_YEAR_BASE + n2 : Number.NaN;
  if (!Number.isInteger(year)) return null;
  return { yearFirst: false, year, a: n0, b: n1 };
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * 依整批判定的順序解析單一日期,回傳 `yyyy-mm-dd`;無法解析回傳 null。
 * 四位年份的形式(`yyyy-mm-dd`)不受 order 影響,一律月在前。
 */
export function parseImportedDate(text: string, order: DateOrder): string | null {
  const parts = parseSegments(text);
  if (!parts) return null;
  const month = parts.yearFirst || order === 'mdy' ? parts.a : parts.b;
  const day = parts.yearFirst || order === 'mdy' ? parts.b : parts.a;
  if (!isValidYmd(parts.year, month, day)) return null;
  return `${String(parts.year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}

/** 把 `yyyy-mm-dd` 轉為 Task 用的 ISODateTime(補上預設時間)。 */
export function toTaskDateTime(date: string): ISODateTime {
  return isoDateTime(`${date}T${DEFAULT_TIME_OF_DAY}`);
}

const WBS_PATTERN = /^\d+(?:\.\d+)*$/;

/** 第一階段解析出的單列資料(尚未解析前置任務 ref)。 */
export interface ParsedPasteRow {
  readonly wbs: string;
  readonly name: string;
  readonly outlineLevel: number;
  readonly durationMinutes: Minutes | null;
  /** `yyyy-mm-dd`,無法解析為 null。 */
  readonly start: string | null;
  readonly finish: string | null;
  /** 前置任務欄的原始文字,留待第二階段解析。 */
  readonly predecessorText: string;
}

export interface ParseMsProjectPasteResult {
  readonly rows: readonly ParsedPasteRow[];
  /** 彙總警告(日期歧義、日期/工期解析失敗、WBS 與縮排不一致等)。 */
  readonly warnings: readonly string[];
  readonly dateOrder: DateOrder;
  readonly dateAmbiguous: boolean;
}

/** 把剪貼簿純文字切成 TSV 格點(去除結尾空白列)。 */
export function splitClipboardGrid(text: string): string[][] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  while (lines.length > 0 && (lines[lines.length - 1] ?? '').trim() === '') lines.pop();
  return lines.map((line) => line.split('\t'));
}

/**
 * 版面配置:
 * - `wbs`   第一欄是 WBS 碼(`WBS | 任務名稱 | 工期 | 開始 | 完成 | 前置任務`)
 * - `indent` 沒有 WBS 欄(使用者只複製了名稱之後的欄位),階層改由名稱的前導空格推導
 */
export type PasteLayout = 'wbs' | 'indent';

const HEADER_LABELS: ReadonlySet<string> = new Set([
  'wbs',
  'id',
  '編號',
  '任務名稱',
  '工作名稱',
  '名稱',
  'name',
  'task name',
  '工期',
  'duration',
  '開始時間',
  '開始',
  'start',
  '完成時間',
  '完成',
  'finish',
  '前置任務',
  'predecessors',
]);

/**
 * 標題列判定必須**整列一起看**:非空欄位中要有過半、且至少兩格命中欄名字典。
 * 只要任一格命中就當標題的話,使用者真的有任務叫「開始」「名稱」時,
 * 第一列資料會被靜默吃掉。
 */
function isHeaderRow(row: readonly string[]): boolean {
  const cells = row.map((cell) => cell.trim()).filter((cell) => cell !== '');
  if (cells.length < 2) return false;
  const matched = cells.filter((cell) => HEADER_LABELS.has(cell.toLowerCase())).length;
  return matched >= 2 && matched * 2 > cells.length;
}

function stripHeaderRow(grid: readonly (readonly string[])[]): readonly (readonly string[])[] {
  const first = grid[0];
  if (!first) return grid;
  if (isHeaderRow(first)) return grid.slice(1);
  // 沒有標題文字、但第一列的第一欄不是 WBS 碼而其餘列都是 → 仍視為標題列。
  const rest = grid.slice(1);
  const firstCell = (first[0] ?? '').trim();
  if (
    rest.length > 0 &&
    firstCell !== '' &&
    !WBS_PATTERN.test(firstCell) &&
    rest.every((row) => WBS_PATTERN.test((row[0] ?? '').trim()))
  ) {
    return rest;
  }
  return grid;
}

/** 名稱欄的前導空白寬度(用來推導階層)。 */
function leadingIndentWidth(text: string): number {
  return /^[ 　\t]*/.exec(text)?.[0]?.length ?? 0;
}

/**
 * 版面「歸類」(寬鬆):只決定欄位怎麼對位,不決定要不要整份匯入。
 * 第一欄全是 WBS 碼就是 `wbs`,否則視為 `indent`(名稱在第 0 欄)。
 */
export function classifyPasteLayout(grid: readonly (readonly string[])[]): PasteLayout {
  const body = stripHeaderRow(grid).filter((row) => row.some((cell) => cell.trim() !== ''));
  if (body.length === 0) return 'wbs';
  const allWbs = body.every((row) => row.length >= 2 && WBS_PATTERN.test((row[0] ?? '').trim()));
  return allWbs ? 'wbs' : 'indent';
}

/** 觸發「整份匯入」所需的最少列數:整份專案表本質上是大批資料。 */
const MIN_IMPORT_ROWS = 5;

/** 日期欄要真的像日期:至少過半列有值,且有值的每一格都解析得出來。 */
function hasDateEvidence(body: readonly (readonly string[])[], columnIndex: number): boolean {
  const cells = body
    .map((row) => (row[columnIndex] ?? '').trim())
    .filter((text) => text !== '');
  if (cells.length * 2 <= body.length) return false;
  const order = detectDateOrder(cells).order;
  return cells.every((text) => parseImportedDate(text, order) !== null);
}

/**
 * 判定是否要走「整份匯入」(會新增列),不符合則回傳 null。
 *
 * 門檻刻意訂得高 —— **寧可漏判也不要誤判**:漏判只是使用者少一次便利,
 * 誤判會讓畫面突然多出幾十列。共同條件:
 * - 標題列之外至少 5 列、每列至少 4 欄
 * - 有多層階層證據(WBS 版面要有含 `.` 的多層碼;縮排版面要有至少兩種縮排深度)
 * - 開始時間欄要真的像日期(過半列有值且全部解析得出)
 * - 工期欄有值的格子都解析得出來,且過半列有工期
 */
export function detectPasteLayout(grid: readonly (readonly string[])[]): PasteLayout | null {
  const body = stripHeaderRow(grid).filter((row) => row.some((cell) => cell.trim() !== ''));
  if (body.length < MIN_IMPORT_ROWS) return null;
  if (!body.every((row) => row.length >= 4)) return null;

  const layout = classifyPasteLayout(grid);
  const nameIndex = layout === 'wbs' ? 1 : 0;
  const durationIndex = nameIndex + 1;
  const startIndex = nameIndex + 2;

  const durationCells = body
    .map((row) => (row[durationIndex] ?? '').trim())
    .filter((text) => text !== '');
  if (durationCells.length * 2 <= body.length) return null;
  if (!durationCells.every((text) => parseDurationInput(text) !== null)) return null;

  if (!hasDateEvidence(body, startIndex)) return null;

  if (layout === 'wbs') {
    // 全部都是頂層編號(1、2、3…)不構成階層,可能只是一般的編號欄。
    const multiLevel = body.some((row) => (row[0] ?? '').trim().includes('.'));
    return multiLevel ? 'wbs' : null;
  }

  const depths = new Set(body.map((row) => leadingIndentWidth(row[nameIndex] ?? '')));
  return depths.size >= 2 ? 'indent' : null;
}

/** 判斷這份剪貼簿內容是否為 MS Project / Excel 匯出的任務表(會觸發整份匯入)。 */
export function looksLikeMsProjectExport(grid: readonly (readonly string[])[]): boolean {
  return detectPasteLayout(grid) !== null;
}

/**
 * 依「出現過的相異縮排寬度排序後的序位」決定層級。
 *
 * 不用 `round(indent / minIndent)` 比例換算 —— 縮排寬度為 0/2/5 時,比例換算會得到
 * 第 1/2/4 層而**跳過第 3 層**;序位換算則得到連續的 1/2/3,任何不規則縮排都成立。
 */
function buildIndentRanks(widths: readonly number[]): ReadonlyMap<number, number> {
  const sorted = [...new Set(widths)].sort((a, b) => a - b);
  return new Map(sorted.map((width, index) => [width, index + 1]));
}

/**
 * 第一階段:把 TSV 格點解析為任務列(名稱、工期、日期、階層)。
 * 單一儲存格解析失敗不會中止整批,只累積警告。
 */
export function parseMsProjectPaste(text: string): ParseMsProjectPasteResult {
  const grid = stripHeaderRow(splitClipboardGrid(text)).filter(
    (row) => row.some((cell) => cell.trim() !== ''),
  );

  // 沒有 WBS 欄時整體左移一欄:名稱 0、工期 1、開始 2、完成 3、前置任務 4。
  // 這裡用寬鬆的 classifyPasteLayout(只決定欄位對位);要不要整份匯入是呼叫端
  // 用 looksLikeMsProjectExport 的嚴格門檻另外決定的。
  const hasWbsColumn = classifyPasteLayout(splitClipboardGrid(text)) === 'wbs';
  const col = {
    wbs: 0,
    name: hasWbsColumn ? 1 : 0,
    duration: hasWbsColumn ? 2 : 1,
    start: hasWbsColumn ? 3 : 2,
    finish: hasWbsColumn ? 4 : 3,
    predecessors: hasWbsColumn ? 5 : 4,
  } as const;

  const dateCells: string[] = [];
  for (const row of grid) {
    for (const index of [col.start, col.finish]) {
      const cell = row[index];
      if (cell && cell.trim() !== '') dateCells.push(cell);
    }
  }
  const detection = detectDateOrder(dateCells);

  // 沒有 WBS 欄時,層級由「相異縮排寬度的排序序位」決定(不寫死 3 格、也不用比例
  // 換算),因此 3 格、2 格或不規則縮排都能得到連續層級。
  // 註:TSV 的 tab 是欄位分隔符,不可能出現在名稱前當縮排,故只處理空白。
  const indentRanks = buildIndentRanks(grid.map((row) => leadingIndentWidth(row[col.name] ?? '')));

  const rows: ParsedPasteRow[] = [];
  const warnings: string[] = [];
  let dateFailures = 0;
  let durationFailures = 0;
  let indentMismatches = 0;

  grid.forEach((row) => {
    const wbs = hasWbsColumn ? (row[col.wbs] ?? '').trim() : '';
    const rawName = row[col.name] ?? '';
    const leadingSpaces = leadingIndentWidth(rawName);

    let outlineLevel: number;
    if (wbs !== '') {
      // WBS 欄存在 → 以 WBS 為準;前導空格只當驗證用的第二訊號(每層 3 格),
      // 不一致時仍採用 WBS 並記警告。
      outlineLevel = wbs.split('.').length;
      const indentLevel = Math.floor(leadingSpaces / SPACES_PER_OUTLINE_LEVEL) + 1;
      if (leadingSpaces > 0 && indentLevel !== outlineLevel) indentMismatches += 1;
    } else {
      // 沒有 WBS 欄 → 用前導空格推導,否則整份表會全部塌成第 1 層。
      outlineLevel = indentRanks.get(leadingSpaces) ?? 1;
    }

    const durationText = (row[col.duration] ?? '').trim();
    let durationMinutes: Minutes | null = null;
    if (durationText !== '') {
      durationMinutes = parseDurationInput(durationText);
      if (durationMinutes === null) durationFailures += 1;
    }

    const start = readDate(row[col.start], detection.order);
    const finish = readDate(row[col.finish], detection.order);
    if (start === 'invalid' || finish === 'invalid') dateFailures += 1;

    rows.push({
      wbs,
      name: rawName.replace(/^[ 　\t]+/, '').trim(),
      outlineLevel,
      durationMinutes,
      start: start === 'invalid' ? null : start,
      finish: finish === 'invalid' ? null : finish,
      predecessorText: (row[col.predecessors] ?? '').trim(),
    });
  });

  if (detection.ambiguous && dateCells.length > 0) {
    warnings.unshift('日期格式無法判定月日順序,已假設為「月-日-年」,請檢查結果是否正確');
  }
  if (dateFailures > 0) warnings.push(`${dateFailures} 筆日期無法解析`);
  if (durationFailures > 0) warnings.push(`${durationFailures} 筆工期無法解析`);
  if (indentMismatches > 0) {
    warnings.push(`${indentMismatches} 筆的縮排與 WBS 階層不一致,已以 WBS 碼為準`);
  }
  return {
    rows,
    warnings,
    dateOrder: detection.order,
    dateAmbiguous: detection.ambiguous,
  };
}

function readDate(cell: string | undefined, order: DateOrder): string | null | 'invalid' {
  const text = (cell ?? '').trim();
  if (text === '') return null;
  const parsed = parseImportedDate(text, order);
  return parsed === null ? 'invalid' : parsed;
}

// ---------------------------------------------------------------------------
// 第二階段:前置任務 ref 解析
// ---------------------------------------------------------------------------

/**
 * 前置任務指向的目標。ref `55` 是「貼上完成後完整清單的第 55 筆」,
 * 可能落在既有任務上(`task`),也可能落在本批新列上(`row`)。
 */
export type PastedPredecessorTarget =
  | { readonly kind: 'task'; readonly taskId: TaskId }
  | { readonly kind: 'row'; readonly rowIndex: number };

export interface PastedPredecessor {
  readonly target: PastedPredecessorTarget;
  readonly type: ParsedPredecessor['type'];
  readonly lagMinutes: Minutes;
  readonly lagIsElapsed: boolean;
}

export interface ResolvePredecessorsResult {
  /** 與 rows 等長:每一列解析後的前置任務清單。 */
  readonly predecessors: readonly (readonly PastedPredecessor[])[];
  readonly warnings: readonly string[];
}

/**
 * 第二階段:在「所有列都已存在」的前提下解析 ref。
 *
 * 投影後的完整清單 = 既有任務的前 startIndex 筆 + 本批 rows(其餘既有任務接在後面)。
 * ref 一律**先當 WBS 碼**比對(先找本批 rows,再找既有任務),與表格前置任務欄的顯示
 * 規則一致(見 dependency-column.ts);比不到且為純數字時,才退回該清單的 1-based 序號。
 */
export function resolvePastedPredecessors(
  rows: readonly ParsedPasteRow[],
  tasks: readonly Task[],
  startIndex: number,
): ResolvePredecessorsResult {
  const warnings: string[] = [];
  const predecessors: (readonly PastedPredecessor[])[] = [];
  const head = tasks.slice(0, Math.max(0, startIndex));
  const tail = tasks.slice(Math.max(0, startIndex) + rows.length);
  const totalCount = head.length + rows.length + tail.length;
  let unresolved = 0;

  const resolve = (ref: string, selfRowIndex: number): PastedPredecessorTarget | null => {
    const wbsRowIndex = rows.findIndex((row) => row.wbs === ref);
    if (wbsRowIndex >= 0) return wbsRowIndex === selfRowIndex ? null : { kind: 'row', rowIndex: wbsRowIndex };
    const byWbs = tasks.find((t) => t.wbs === ref);
    if (byWbs !== undefined) return { kind: 'task', taskId: byWbs.id };
    // 含點的 ref 只可能是 WBS 碼;比不到就是找不到,不會誤解成列號。
    if (ref.includes('.')) return null;
    const n = Number(ref);
    if (!Number.isInteger(n) || n < 1 || n > totalCount) return null;
    const zero = n - 1;
    if (zero < head.length) {
      const task = head[zero];
      return task ? { kind: 'task', taskId: task.id } : null;
    }
    const rowIndex = zero - head.length;
    if (rowIndex < rows.length) {
      if (rowIndex === selfRowIndex) return null;
      return { kind: 'row', rowIndex };
    }
    const task = tail[rowIndex - rows.length];
    return task ? { kind: 'task', taskId: task.id } : null;
  };

  rows.forEach((row, rowIndex) => {
    if (row.predecessorText === '') {
      predecessors.push([]);
      return;
    }
    const parsed = parsePredecessorInput(row.predecessorText);
    if (parsed === null) {
      unresolved += 1;
      warnings.push(`第 ${rowIndex + 1} 列前置任務「${row.predecessorText}」無法解析`);
      predecessors.push([]);
      return;
    }
    const items: PastedPredecessor[] = [];
    for (const item of parsed) {
      const target = resolve(item.ref, rowIndex);
      if (target === null) {
        unresolved += 1;
        warnings.push(`第 ${rowIndex + 1} 列找不到前置任務「${item.ref}」`);
        continue;
      }
      items.push({
        target,
        type: item.type,
        lagMinutes: item.lagMinutes,
        lagIsElapsed: item.lagIsElapsed,
      });
    }
    predecessors.push(items);
  });

  if (unresolved > 0) warnings.push(`${unresolved} 筆前置任務無法對應`);
  return { predecessors, warnings };
}

// ---------------------------------------------------------------------------
// 對外交付給 TaskGrid 上層的資料結構
// ---------------------------------------------------------------------------

/** 交給上層建立/覆蓋的一列任務(欄位為 null 代表該格空白或解析失敗,不要覆蓋)。 */
export interface PastedTask {
  readonly wbs: string;
  readonly name: string;
  readonly outlineLevel: number;
  readonly durationMinutes: Minutes | null;
  readonly start: ISODateTime | null;
  readonly finish: ISODateTime | null;
  readonly predecessors: readonly PastedPredecessor[];
}

export interface PasteImportResult {
  readonly tasks: readonly PastedTask[];
  /** 從完整清單的第幾筆(0-based)開始覆蓋;超出既有列數的部分為新增列。 */
  readonly startIndex: number;
  readonly warnings: readonly string[];
}

/**
 * 完整的兩階段匯入:先解析所有列,再解析前置任務 ref。
 *
 * 順序很重要 —— ref `55` 指「完整清單第 55 筆」,若在所有列都建立之前就解析,
 * 會產生大量「找不到 ref」的假錯誤。
 */
export function buildPasteImport(
  text: string,
  tasks: readonly Task[],
  startIndex: number,
): PasteImportResult {
  const parsed = parseMsProjectPaste(text);
  const resolved = resolvePastedPredecessors(parsed.rows, tasks, startIndex);

  const imported: PastedTask[] = parsed.rows.map((row, index) => ({
    wbs: row.wbs,
    name: row.name,
    outlineLevel: row.outlineLevel,
    durationMinutes: row.durationMinutes,
    start: row.start === null ? null : toTaskDateTime(row.start),
    finish: row.finish === null ? null : toTaskDateTime(row.finish),
    predecessors: resolved.predecessors[index] ?? [],
  }));

  return {
    tasks: imported,
    startIndex,
    warnings: [...parsed.warnings, ...resolved.warnings],
  };
}

/** 把警告彙總成單行訊息(供錯誤橫幅顯示)。 */
export function summarizePasteWarnings(rowCount: number, warnings: readonly string[]): string | null {
  if (warnings.length === 0) return null;
  const shown = warnings.slice(0, 3).join(';');
  const rest = warnings.length > 3 ? `(另有 ${warnings.length - 3} 項)` : '';
  return `已貼上 ${rowCount} 列,${shown}${rest}`;
}
